import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Spinner } from "@shopify/polaris";
import { X, Minus, Maximize2, Phone } from "lucide-react";
import { RootState, AppDispatch } from "configureStore";
import { useAppSelector } from "hooks";
import UserAvatar from "components/user-avatar/user-avatar";
import { ChatMessage, Conversation, ConversationType } from "types/Chat";
import {
  sendMessage as sendMessageApi,
  markConversationRead,
  addReaction,
  removeReaction,
  editMessage as editMessageApi,
  deleteMessage as deleteMessageApi,
} from "services/chat.service";
import {
  fetchMessagesThunk,
  fetchMiniChatConversationMetaThunk,
  addOptimisticMessage,
  replaceOptimisticMessage,
  removeOptimisticMessage,
  clearUnread,
  closeMiniChat,
  toggleMinimizeMiniChat,
  setMiniChatActive,
  applyReactionUpdate,
  applyMessageEdited,
  applyMessageDeleted,
} from "./chat.slice";
import { useCalls } from "features/calls/calls-provider";
import ChatComposer from "./chat-composer";
import ChatMessageContent from "./chat-message-content";
import { MessageStatus } from "./message-status";
import { uploadChatFileMessage } from "./chat-file-upload";
import { 
  ReactionPills, 
  ReactionPicker, 
  computeOptimisticReactions,
} from "./chat-reactions";
import { FINGER_PADDING, SHOW_ON_HOVER_OR_TOUCH } from "./chat-touch";
import { toast } from "sonner";

// Keep in sync with `Messages.EDIT_WINDOW_MS` in erp-backend/api/models/Messages.js
// and the matching constant in chat-tab.tsx.
const EDIT_WINDOW_MS = 5 * 60 * 1000;

// Header buttons: 44px below the desktop width (icon grown to 20px), the small desktop size otherwise.
const HEADER_BUTTON_CLASS = "p-1 max-lg:p-3 hover:bg-gray-600 rounded text-gray-400";
const HEADER_ICON_CLASS = "max-lg:h-5 max-lg:w-5";

interface Props {
  companyId: string;
  conversationId: string;
  isMinimized: boolean;
}

export const MiniChatBox: React.FC<Props> = ({ companyId, conversationId, isMinimized }) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const session = useAppSelector((state) => state.authReducer.session);
  const myUserId = session?.id;
  const [openPickerId, setOpenPickerId] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingValue, setEditingValue] = React.useState("");
  
  const convoFromList = useSelector((state: RootState) =>
    state.chatReducer.conversations.find((c) => c.id === conversationId)
  );
  const miniChatConversations = useSelector(
    (state: RootState) => state.chatReducer.miniChatConversations
  );
  const convoFromMiniCache = miniChatConversations[conversationId];
  const convoResolved = convoFromList || convoFromMiniCache;
  
  const presenceOnline = useSelector((state: RootState) => state.presenceReducer.online);
  const presenceOffline = useSelector((state: RootState) => state.presenceReducer.offline);
  const presenceSelf = useSelector((state: RootState) => state.presenceReducer.self);

  const userById = useMemo(() => {
    const m = new Map<string, { id: string; name: string; avatarFileId: string | null }>();
    if (presenceSelf) {
      m.set(presenceSelf.id, {
        id: presenceSelf.id, 
        name: presenceSelf.name, 
        avatarFileId: presenceSelf.avatarFileId || null
      });
    }
    for (const u of presenceOnline) {
      m.set(u.id, { id: u.id, name: u.name, avatarFileId: u.avatarFileId || null });
    }
    for (const u of presenceOffline) {
      m.set(u.id, { id: u.id, name: u.name, avatarFileId: u.avatarFileId || null });
    }
    if (session) {
      m.set(session.id, {
        id: session.id,
        name: session.name || "",
        avatarFileId: session.avatarFileId || null
      });
    }
    
    // Fallback: include the DM participant since cross-company users may not be in the active presence state
    if (convoResolved?.otherParticipant) {
      m.set(convoResolved.otherParticipant.id, convoResolved.otherParticipant);
    }
    return m;
  }, [presenceSelf, presenceOnline, presenceOffline, session, convoResolved]);

  const handleToggleReaction = async (messageId: string, emoji: string) => {
    const msg = thread?.messages.find(m => m.id === messageId);
    if (!msg) return;

    const optimistic = computeOptimisticReactions(msg.reactions || [], emoji);
    dispatch(applyReactionUpdate({ conversationId, messageId, reactions: optimistic }));

    const myExisting = (msg.reactions || []).find((r) => r.me);
    const isToggleOff = !!myExisting && myExisting.emoji === emoji;

    try {
      const reactions = isToggleOff
        ? await removeReaction(companyId, messageId)
        : await addReaction(companyId, messageId, emoji);
      dispatch(applyReactionUpdate({ conversationId, messageId, reactions }));
    } catch {
      // Revert on error
      dispatch(fetchMessagesThunk({ companyId, conversationId }));
    }
  };
  const handleEdit = async (messageId: string) => {
    const content = editingValue.trim();
    if (!content) return;
    try {
      const updated = await editMessageApi(companyId, messageId, content);
      dispatch(applyMessageEdited({ 
        conversationId, 
        messageId, 
        content: updated.content, 
        editedAt: updated.editedAt! 
      }));
      setEditingId(null);
    } catch {
      toast.error(t("error"));
    }
  };

  const handleDelete = async (messageId: string) => {
    try {
      await deleteMessageApi(companyId, messageId);
      dispatch(applyMessageDeleted({ conversationId, messageId }));
    } catch {
      toast.error(t("error"));
    }
  };

  const miniChatMeta = useSelector((state: RootState) =>
    state.chatReducer.activeMiniChats.find(
      (c) => String(c.conversationId) === String(conversationId)
    )
  );
  const thread = useSelector(
    (state: RootState) => state.chatReducer.threads[conversationId]
  );
  const companies = useSelector(
    (state: RootState) => state.companySelectReducer?.companiesCollectionRaw || []
  ) as Array<{ id: string; name: string; legalName?: string }>;
  const unreadByCompany = useSelector(
    (state: RootState) => state.chatReducer.unreadByCompany
  );

  const companyDisplayName = useMemo(() => {
    const match = companies.find((c) => String(c.id) === String(companyId));
    if (match?.legalName) return match.legalName;
    if (match?.name) return match.name;
    return unreadByCompany[companyId]?.name || "";
  }, [companies, companyId, unreadByCompany]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Load full history (socket may have created a 1-message thread first).
  useEffect(() => {
    if (!companyId || !conversationId) return;
    if (thread?.historyLoaded) return;
    dispatch(
      fetchMessagesThunk({ companyId, conversationId, limit: 100 })
    );
  }, [companyId, conversationId, thread?.historyLoaded, dispatch]);

  // Cross-company: fetch participant name/avatar into miniChatConversations cache.
  useEffect(() => {
    if (!companyId || !conversationId) return;
    if (convoFromList || convoFromMiniCache) return;
    dispatch(fetchMiniChatConversationMetaThunk({ companyId, conversationId }));
  }, [companyId, conversationId, convoFromList, convoFromMiniCache, dispatch]);

  // Reset picker/editing state when this mini-window switches conversations
  // (defensive — typically each MiniChatBox instance is one conversation,
  // but the dependency on conversationId guarantees no leak across reuses).
  useEffect(() => {
    setOpenPickerId(null);
    setEditingId(null);
    setEditingValue("");
  }, [conversationId]);

  // Mark as read when messages arrive or box is opened/maximized
  useEffect(() => {
    if (!companyId || !conversationId || isMinimized) return;
    const timeout = setTimeout(async () => {
      try {
        const res = await markConversationRead(companyId, conversationId);
        dispatch(clearUnread({ conversationId, lastReadAt: res.lastReadAt }));
      } catch { /* ignore */ }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [companyId, conversationId, isMinimized, thread?.messages.length, dispatch]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (isMinimized) return;
    const el = scrollerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [thread?.messages.length, isMinimized]);

  const handleSend = async (content: string) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimistic: ChatMessage = {
      id: tempId,
      conversationId,
      sender: myUserId || "",
      content,
      attachment: null,
      createdAt: Date.now(),
      editedAt: null,
      deletedAt: null,
      reactions: [],
      uploadStatus: "pending",
    };
    dispatch(addOptimisticMessage(optimistic));
    dispatch(setMiniChatActive(conversationId));
    try {
      const real = await sendMessageApi(companyId, conversationId, { content });
      dispatch(replaceOptimisticMessage({ tempId, message: real }));
    } catch (err: any) {
      dispatch(removeOptimisticMessage({ conversationId, tempId }));
      toast.error(err?.response?.data?.error || t("error"));
      throw err;
    }
  };

  const handleFileSelected = async (file: File) => {
    await uploadChatFileMessage({
      dispatch,
      companyId,
      conversationId,
      myUserId,
      file,
      fileUploadFailedMessage: t("file_upload_failed"),
      onOptimisticMessageAdded: () => dispatch(setMiniChatActive(conversationId)),
      onError: toast.error,
    });
  };

  // Cross-company mini chats are not in `conversations` (that list is scoped to
  // the active company). Fall back to metadata stored on activeMiniChats.
  const convoType: ConversationType =
    convoResolved?.type || miniChatMeta?.conversationType || "dm";
  const isCompanyChat = convoType === "company";

  const title = isCompanyChat
    ? t("company_chat")
    : convoResolved?.otherParticipant?.name ||
      miniChatMeta?.title ||
      "";

  // Calls are 1:1, so only a DM with a resolved other participant offers one.
  const { enabled: callsEnabled, busyConversationId, startCall } = useCalls();
  const otherParticipant = convoResolved?.otherParticipant;
  const callPeer =
    callsEnabled && !isCompanyChat && otherParticipant?.id
      ? { userId: String(otherParticipant.id), name: otherParticipant.name || title }
      : null;

  const avatarUser =
    !isCompanyChat &&
    (convoResolved?.otherParticipant || miniChatMeta?.title)
      ? {
          id: convoResolved?.otherParticipant?.id || "",
          name: convoResolved?.otherParticipant?.name || miniChatMeta?.title || "",
          avatarFileId:
            convoResolved?.otherParticipant?.avatarFileId ??
            miniChatMeta?.avatarFileId ??
            null,
        }
      : null;

  const loadOlder = () => {
    if (!thread?.hasMore || thread.isLoadingOlder || thread.nextCursor == null) return;
    dispatch(
      fetchMessagesThunk({
        companyId,
        conversationId,
        cursor: thread.nextCursor,
        limit: 50,
      })
    );
  };

  const convoForStatus: Conversation | null = convoResolved || (miniChatMeta
    ? {
        id: conversationId,
        type: convoType,
        lastMessage: null,
        lastReadAt: null,
        unreadCount: 0,
        maxOtherLastReadAt: null,
        otherParticipant: avatarUser
          ? {
              id: avatarUser.id,
              name: avatarUser.name,
              avatarFileId: avatarUser.avatarFileId,
              lastReadAt: null,
            }
          : null,
      }
    : null);

  const isOnline = !!(avatarUser?.id && presenceOnline.some((u) => u.id === avatarUser.id));

  return (
    <div 
      className={`flex flex-col bg-gray-900 border border-gray-700 shadow-2xl rounded-t-lg transition-all duration-200 ${
        isMinimized
          ? "w-52"
          // Fits a phone: the launcher button beside the window takes 6.5rem (button, gap and
          // the container's padding); the 5rem of height is the top bar (4rem) plus the
          // container's bottom padding (1rem).
          : "h-[min(460px,calc(100dvh-var(--phone-bar-height,0px)-5rem))] w-[min(360px,calc(100vw-6.5rem))]"
      } group/chat z-[400]`}
      onClick={() => dispatch(setMiniChatActive(conversationId))}
    >
      {/* Header */}
      <div 
        className={`shrink-0 px-2 flex items-center justify-between border-b border-gray-800 bg-gray-800 cursor-pointer hover:bg-gray-700 ${
          isMinimized ? "min-h-10 py-1" : "min-h-12 py-1.5"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          dispatch(toggleMinimizeMiniChat(conversationId));
        }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {avatarUser ? (
            <UserAvatar companyId={companyId} user={avatarUser} size={24} online={isOnline} />
          ) : (
            <div className="w-6 h-6 rounded bg-emerald-700 flex items-center justify-center text-[10px] font-bold text-white shrink-0">#</div>
          )}
          <div className="min-w-0 flex-1 leading-tight">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs font-semibold truncate text-gray-100">
                {title || (!convoResolved && !miniChatMeta?.title ? t("loading") : "")}
              </span>
              {isOnline && !isMinimized && (
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] shrink-0" />
              )}
            </div>
            {companyDisplayName && (
              <span
                className="block text-[10px] text-gray-400 truncate"
                title={companyDisplayName}
              >
                {companyDisplayName}
              </span>
            )}
          </div>
          {(convoResolved?.unreadCount || 0) > 0 && isMinimized && (
            <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px] text-center shrink-0">{convoResolved!.unreadCount}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {callPeer && (
            <button
              className={`${HEADER_BUTTON_CLASS} disabled:opacity-40`}
              disabled={!!busyConversationId}
              title={t("call_start")}
              onClick={(e) => {
                e.stopPropagation();
                startCall(companyId, conversationId, callPeer);
              }}
            >
              <Phone size={14} className={HEADER_ICON_CLASS} />
            </button>
          )}
          <button 
            className={HEADER_BUTTON_CLASS}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(toggleMinimizeMiniChat(conversationId));
            }}
          >
            {isMinimized ? (
              <Maximize2 size={14} className={HEADER_ICON_CLASS} />
            ) : (
              <Minus size={14} className={HEADER_ICON_CLASS} />
            )}
          </button>
          <button 
            className={HEADER_BUTTON_CLASS}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(closeMiniChat(conversationId));
            }}
          >
            <X size={14} className={HEADER_ICON_CLASS} />
          </button>
        </div>
      </div>

      {/* Body */}
      {!isMinimized && (
        <>
          <div 
            ref={scrollerRef}
            className="flex-1 overflow-y-auto px-2 py-3 flex flex-col gap-2 [scrollbar-width:thin] bg-gray-900"
          >
            {!thread?.historyLoaded && !(thread?.messages?.length) ? (
              <div className="flex justify-center py-4">
                <Spinner size="small" />
              </div>
            ) : thread && thread.messages.length === 0 && thread.historyLoaded ? (
              <div className="text-[10px] text-gray-500 text-center py-4">{t("no_messages_yet")}</div>
            ) : thread ? (
              <>
              {!thread.historyLoaded && (
                <div className="flex justify-center py-1">
                  <Spinner size="small" />
                </div>
              )}
              {thread.hasMore && (
                <button
                  type="button"
                  onClick={loadOlder}
                  disabled={thread.isLoadingOlder}
                  className="self-center text-[10px] max-md:text-xs max-lg:min-h-11 text-blue-400 hover:text-blue-300 disabled:opacity-50 py-1"
                >
                  {thread.isLoadingOlder
                    ? t("loading")
                    : t("load_older_messages", { defaultValue: "Load older messages" })}
                </button>
              )}
              {thread.messages.map((m, idx) => {
                const mine = String(m.sender) === String(myUserId);
                const isOptimistic = m.id.startsWith("temp-");
                const isEditing = editingId === m.id;
                const canEdit = mine && !m.deletedAt && !isOptimistic
                  && (Date.now() - m.createdAt < EDIT_WINDOW_MS);
                const canDelete = mine && !m.deletedAt && !isOptimistic;
                const prev = idx > 0 ? thread.messages[idx - 1] : null;
                const showAuthorHeader = !mine && (
                  idx === 0 ||
                  !prev ||
                  String(prev.sender) !== String(m.sender) ||
                  m.createdAt - prev.createdAt > 5 * 60 * 1000
                );
                const author = userById.get(m.sender) || { id: m.sender, name: "", avatarFileId: null };

                return (
                  <React.Fragment key={m.id}>
                    {showAuthorHeader && (
                      <div className="flex items-center gap-1.5 mt-2 mb-0.5 self-start">
                        <UserAvatar companyId={companyId} user={author} size={16} />
                        <span className="text-[10px] text-gray-400 font-medium truncate">{author.name || m.sender}</span>
                      </div>
                    )}
                    {/* Same column structure as main chat (chat-tab.tsx):
                        bubble → reaction pills → footer (timestamp + picker + edit/delete + status). */}
                    <div
                      className={`flex flex-col max-w-[90%] group ${mine ? "self-end items-end" : "self-start items-start"}`}
                    >
                      {isEditing ? (
                        <div className="flex flex-col w-full gap-1">
                          <textarea
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            rows={2}
                            className="w-full bg-gray-800 text-gray-100 text-base lg:text-xs px-2 py-1 rounded border border-blue-500 focus:outline-none"
                            autoFocus
                          />
                          <div className="flex gap-1 justify-end">
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="text-[10px] max-md:text-xs max-lg:min-h-11 max-lg:px-3 px-2 py-0.5 rounded text-gray-400 hover:bg-gray-800"
                            >
                              {t("cancel")}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(m.id)}
                              className="text-[10px] max-md:text-xs max-lg:min-h-11 max-lg:px-3 px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              {t("save")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={
                            "rounded-lg text-sm whitespace-pre-wrap break-words px-2.5 py-1.5 " +
                            (mine
                              ? `bg-blue-600 text-white ${isOptimistic ? "opacity-60" : ""}`
                              : "bg-gray-800 text-gray-100")
                          }
                        >
                          {m.deletedAt ? (
                            <span className="italic text-gray-400">{t("message_deleted")}</span>
                          ) : (
                            <ChatMessageContent message={m} mine={mine} companyId={companyId} />
                          )}
                        </div>
                      )}
                      {!m.deletedAt && (
                        <ReactionPills
                          reactions={m.reactions || []}
                          onToggle={(e) => handleToggleReaction(m.id, e)}
                        />
                      )}
                      <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2">
                        <span>
                          {new Date(m.createdAt).toLocaleTimeString(undefined, {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {m.editedAt && !m.deletedAt && (
                            <span className="italic"> ({t("edited")})</span>
                          )}
                        </span>
                        {!isEditing && !m.deletedAt && !isOptimistic && (
                          <ReactionPicker
                            open={openPickerId === m.id}
                            onOpenChange={(o) => setOpenPickerId(o ? m.id : null)}
                            onPick={(e) => handleToggleReaction(m.id, e)}
                          />
                        )}
                        {!isEditing && (canEdit || canDelete) && (
                          <span className={`${SHOW_ON_HOVER_OR_TOUCH} flex gap-1`}>
                            {canEdit && (
                              <button
                                type="button"
                                className={`${FINGER_PADDING} hover:text-gray-300`}
                                onClick={() => { setEditingId(m.id); setEditingValue(m.content); }}
                              >
                                {t("edit")}
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                className={`${FINGER_PADDING} hover:text-red-400`}
                                onClick={() => handleDelete(m.id)}
                              >
                                {t("delete")}
                              </button>
                            )}
                          </span>
                        )}
                        {mine && !m.deletedAt && convoForStatus && (
                          <MessageStatus
                            message={m}
                            conversation={convoForStatus}
                            companyId={companyId}
                          />
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              </>
            ) : null}
          </div>
          <ChatComposer 
            onSend={handleSend} 
            onFileSelected={handleFileSelected}
            placeholder={t("type_a_message")}
            compact
          />
        </>
      )}
    </div>
  );
};
